import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons'
import { useQuery } from '@tanstack/react-query'
import { Company } from 'depoto-core'
import { useCore } from '../hooks'

const API_URL = '/api-overdue'
const SNOOZE_MS = 60 * 60 * 1000
const STORAGE_KEY = 'unpaidInvoicesWarningDismissedAt'
const CONTACT_EMAIL = 'info@tomatom.cz'
const REFETCH_MS = 10 * 60 * 1000

type OverdueInvoice = {
  variable: number
  client_name: string
  price: number
  currency: string
  due_date: string
  alert1: string
  alert2: string
  alert3: string
}

type OverdueResponse = {
  count: number
  data: OverdueInvoice[]
}

const dayStart = (value: string): number => {
  const [year, month, day] = value.split('-').map(Number)
  return new Date(year, month - 1, day).getTime()
}

const todayStart = (): number => {
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
}

const formatDueDate = (dueDate: string) => {
  const [year, month, day] = dueDate.split('-')
  return `${Number(day)}. ${Number(month)}. ${year}`
}

const formatPrice = (price: number, currency: string) =>
  `${new Intl.NumberFormat('cs-CZ', { maximumFractionDigits: 2 }).format(price)} ${currency}`

const pad = (n: number) => String(n).padStart(2, '0')

const formatCountdown = (deadline: number): string => {
  const diff = Math.max(0, deadline - Date.now())
  const days = Math.floor(diff / (24 * 60 * 60 * 1000))
  const hours = Math.floor((diff / (60 * 60 * 1000)) % 24)
  const minutes = Math.floor((diff / (60 * 1000)) % 60)
  const seconds = Math.floor((diff / 1000) % 60)
  const daysLabel = days === 1 ? 'den' : days >= 2 && days <= 4 ? 'dny' : 'dní'
  return `${days} ${daysLabel} ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
}

const isSnoozed = (storageKey: string): boolean => {
  const dismissedAt = Number(window.localStorage.getItem(storageKey) || 0)
  return Date.now() - dismissedAt < SNOOZE_MS
}

type Registration = { ic: string; dic: string }

const isFilled = (value?: string | null) => {
  const trimmed = value?.trim()
  return Boolean(trimmed && trimmed !== '0')
}

const registration = (ic?: string | null, dic?: string | null): Registration | undefined => {
  if (!isFilled(ic) || !isFilled(dic)) {
    return undefined
  }
  return { ic: ic!.trim(), dic: dic!.trim() }
}

const billingRegistrations = (company?: Company): Registration[] => {
  const parent = registration(company?.parent?.ic, company?.parent?.dic)
  const own = registration(company?.ic, company?.dic)
  const unique = new Map<string, Registration>()
  ;[parent, own].forEach(item => {
    if (!item) {
      return
    }
    unique.set(`${item.ic}|${item.dic}`, item)
  })
  return [...unique.values()]
}

const fetchOverdueInvoices = async (ic: string, dic: string): Promise<OverdueInvoice[]> => {
  const url = new URL(API_URL, window.location.origin)
  url.searchParams.set('ic', ic)
  url.searchParams.set('dic', dic)
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`Overdue invoices request failed: ${response.status}`)
  }
  const body = (await response.json()) as OverdueResponse
  if (!Array.isArray(body?.data)) {
    return []
  }
  return body.data.filter(invoice => invoice?.alert1 && invoice?.alert3)
}

export const UnpaidInvoicesWarning: React.FC = () => {
  const { company } = useCore()
  const registrations = useMemo(
    () => billingRegistrations(company),
    [company?.ic, company?.dic, company?.parent?.ic, company?.parent?.dic],
  )
  const storageKey = registrations.length
    ? `${STORAGE_KEY}:${registrations.map(item => item.ic).join(',')}`
    : STORAGE_KEY
  const [isOpen, setIsOpen] = useState(false)
  const [now, setNow] = useState(() => Date.now())

  const overdueQuery = useQuery({
    queryKey: ['overdue-invoices', registrations],
    queryFn: async () => {
      const batches = await Promise.all(registrations.map(item => fetchOverdueInvoices(item.ic, item.dic)))
      const byVariable = new Map<number, OverdueInvoice>()
      batches.flat().forEach(invoice => byVariable.set(invoice.variable, invoice))
      return [...byVariable.values()]
    },
    enabled: registrations.length > 0,
    refetchInterval: REFETCH_MS,
    retry: 1,
  })
  const invoices = overdueQuery.data ?? []

  const visibleInvoices = useMemo(() => {
    const today = todayStart()
    return invoices.filter(invoice => today >= dayStart(invoice.alert1))
  }, [invoices, now])

  const blockAt = useMemo(() => {
    const deadlines = visibleInvoices.map(invoice => dayStart(invoice.alert3))
    return deadlines.length > 0 ? Math.min(...deadlines) : undefined
  }, [visibleInvoices])

  const isBlocked = blockAt !== undefined && todayStart() >= blockAt

  useEffect(() => {
    if (invoices.length === 0) {
      setIsOpen(false)
      return
    }
    const check = () => {
      const today = todayStart()
      const visible = invoices.filter(invoice => today >= dayStart(invoice.alert1))
      setNow(Date.now())
      if (visible.length === 0) {
        setIsOpen(false)
        return
      }
      const blocked = visible.some(invoice => today >= dayStart(invoice.alert3))
      if (blocked || !isSnoozed(storageKey)) {
        setIsOpen(true)
      }
    }
    check()
    const interval = setInterval(check, 30 * 1000)
    return () => clearInterval(interval)
  }, [invoices, storageKey])

  useEffect(() => {
    if (!isOpen || isBlocked) {
      return
    }
    const interval = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(interval)
  }, [isOpen, isBlocked])

  const dismiss = useCallback(() => {
    if (isBlocked) {
      return
    }
    window.localStorage.setItem(storageKey, String(Date.now()))
    setIsOpen(false)
  }, [isBlocked, storageKey])

  if (!isOpen || visibleInvoices.length === 0) {
    return null
  }

  const overdueCount = visibleInvoices.length

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/55">
      <div className="flex h-[80%] w-[80%] flex-col items-center justify-center overflow-y-auto rounded-xl border border-[#A32D2D] bg-[#F7C1C1] p-8 text-center">
        <div className="mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-[#E24B4A]">
          <FontAwesomeIcon icon={faTriangleExclamation} className="text-white" size="2x" />
        </div>
        <div className="text-2xl font-medium text-[#791F1F]">Neuhrazené faktury</div>
        <p className="mt-2 text-base text-[#791F1F]">
          Na vašem účtu {overdueCount === 1 ? 'je' : overdueCount <= 4 ? 'jsou' : 'je'} {overdueCount}{' '}
          {overdueCount === 1 ? 'faktura' : overdueCount <= 4 ? 'faktury' : 'faktur'} po splatnosti:
        </p>

        <div className="mt-3 w-full max-w-md overflow-hidden rounded-lg border border-[#A32D2D]/30 bg-white">
          {visibleInvoices.map((invoice, i) => (
            <div
              key={invoice.variable}
              className={`flex items-center justify-between gap-3 px-4 py-2 ${i > 0 ? 'border-t border-slate-200' : ''}`}>
              <span className="font-mono text-sm text-black">VS {invoice.variable}</span>
              <span className="text-sm text-black">{formatPrice(invoice.price, invoice.currency)}</span>
              <span className="text-sm font-medium text-[#A32D2D]">splatnost {formatDueDate(invoice.due_date)}</span>
            </div>
          ))}
        </div>

        {isBlocked ? (
          <p className="mt-5 max-w-md text-base font-medium text-[#791F1F]">
            Lhůta vypršela. Přístup k aplikaci je pozastaven, dokud faktury neuhradíte.
          </p>
        ) : (
          <>
            <div className="mt-5">
              <div className="text-sm font-medium text-[#791F1F]">Přístup bude zablokován za</div>
              <div className="mt-1 font-mono text-4xl font-medium tracking-wide text-[#791F1F]">
                {blockAt !== undefined ? formatCountdown(blockAt) : ''}
              </div>
            </div>
            <p className="mt-4 max-w-md text-xs leading-relaxed text-[#791F1F]">
              Toto upozornění se opakuje každou hodinu, dokud faktury neuhradíte. Po vypršení lhůty bude přístup k
              aplikaci pozastaven.
            </p>
          </>
        )}
        <div className="mt-5 flex gap-3">
          {!isBlocked && (
            <button
              type="button"
              onClick={dismiss}
              className="rounded-lg border border-slate-300 bg-white px-5 py-2 text-sm text-black hover:bg-slate-100">
              Rozumím, zavřít
            </button>
          )}
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="rounded-lg border border-slate-300 bg-white px-5 py-2 text-sm text-black hover:bg-slate-100">
            Kontaktovat účtárnu
          </a>
        </div>
      </div>
    </div>
  )
}
